import { useBotUser } from '@/stores/useBotUser';
import * as PleasedApi from '@/common/api/api.pleased';
import type { TransStatement } from '@/common/api/model';
import type { TransactionMetadata } from '@/common/api/models.pleased';
import { useConfig } from '@/stores/useConfig';
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { logger } from '@/common/api/logger';
import { parseErrorMessage } from '@/common/error';
import { devLog } from '@/common/debug';

/**
 * Fetch metadata for a single transaction
 */
export async function fetchTransactionMetadata(
  tradeId: string,
  userId: string | number,
  countryCode: string
): Promise<TransactionMetadata> {
  return PleasedApi.getTransactionMetadata({
    tradeId,
    userId,
    countryCode,
  });
}

/**
 * Enrich a single transaction with metadata
 */
export async function enrichTransactionWithMetadata(
  transaction: TransStatement,
  userId: string | number,
  countryCode: string
): Promise<TransStatement & Partial<TransactionMetadata>> {
  try {
    const metadata = await fetchTransactionMetadata(transaction.tradeId, userId, countryCode);
    return {
      ...transaction,
      ...metadata,
    };
  } catch (error) {
    logger.error(`[USE_TRANSACTION_METADATA] Failed to fetch metadata for transaction ${transaction.tradeId}:`, {
      error: parseErrorMessage(error),
      tradeId: transaction.tradeId,
    });
    return transaction;
  }
}

/**
 * Enrich a list of transactions with metadata
 * Fetches metadata for each transaction in parallel
 */
export async function enrichTransactionsWithMetadata(
  transactions: TransStatement[],
  userId: string | number,
  countryCode: string
): Promise<Array<TransStatement & Partial<TransactionMetadata>>> {
  const enrichmentPromises = transactions.map((transaction) =>
    enrichTransactionWithMetadata(transaction, userId, countryCode)
  );

  return Promise.all(enrichmentPromises);
}

/**
 * Composable for fetching and enriching transactions with metadata
 */
export function useTransactionMetadata() {
  const { user } = storeToRefs(useBotUser());
  const { config } = storeToRefs(useConfig());
  const countryCode = computed(() => config.value?.country || '');

  /**
   * Fetch metadata for a single transaction using current user context
   */
  const fetchMetadata = async (tradeId: string): Promise<TransactionMetadata> => {
    if (!user.value.foreignId) {
      throw new Error('User ID is required to fetch transaction metadata');
    }

    return fetchTransactionMetadata(tradeId, user.value.foreignId, countryCode.value);
  };

  /**
   * Enrich multiple transactions with metadata using current user context
   */
  const enrichTransactions = async (
    transactions: TransStatement[]
  ): Promise<Array<TransStatement & Partial<TransactionMetadata>>> => {
    if (!user.value.foreignId) {
      devLog.warn('User ID not available, returning transactions without metadata');
      return transactions;
    }

    return enrichTransactionsWithMetadata(transactions, user.value.foreignId, countryCode.value);
  };

  return {
    fetchMetadata,
    enrichTransactions,
  };
}
